/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * @param {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo|null|undefined} model
 * @param {Object|null|undefined} legacyPlayer
 */
function resolvePlayer(model, legacyPlayer) {
  return model?.player ?? legacyPlayer ?? null;
}

/**
 * @param {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo|null|undefined} model
 * @param {boolean} legacyInitialized
 */
function isPlayerReady(model, legacyInitialized) {
  if (model?.isInitialized) {
    return model.isInitialized();
  }

  return !!legacyInitialized;
}

/**
 * Video Pilot
 *
 * Object-based pattern for video autopilot behaviors.
 * Allows downstream packages to override specific behavior handlers.
 */
export const videoPilot = {
  /**
   * @param {Object} params
   * @param {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo|null|undefined} [params.video]
   * @param {Object} [params.player] - legacy; prefer params.video
   * @param {boolean} [params.initialized] - legacy
   * @param {Function} params.init
   * @param {string[]} params.behaviors
   */
  async enter({video, player, initialized, init, behaviors = ['init', 'autoplay']}) {
    let model = video;

    for (const action of behaviors) {
      try {
        let currentPlayer = resolvePlayer(model, player);
        let currentInitialized = isPlayerReady(model, initialized);

        if (action === 'init' && !currentPlayer) {
          const created = await init?.();

          if (created) {
            model = created;
          }

          currentPlayer = resolvePlayer(model, player);
          currentInitialized = isPlayerReady(model, initialized);
        } else if (action === 'autoplay' && currentPlayer && currentInitialized) {
          await currentPlayer.play();
        } else if (action === 'resume' && currentPlayer && currentInitialized) {
          const currentTime = await currentPlayer.call('getCurrentTime').catch(() => 0);

          if (currentTime > 0) {
            await currentPlayer.play();
          }
        }
      } catch (error) {
        if (error.name !== 'NotAllowedError') {
          console.log(`Autopilot action "${action}" failed:`, error);
        }
      }
    }
  },

  /**
   * @param {Object} params
   * @param {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo|null|undefined} [params.video]
   * @param {Object} [params.player]
   * @param {boolean} [params.initialized]
   * @param {string[]} params.behaviors
   */
  async leave({video, player, initialized, behaviors = ['autopause']}) {
    for (const action of behaviors) {
      try {
        const currentPlayer = resolvePlayer(video, player);
        const currentInitialized = isPlayerReady(video, initialized);

        if (action === 'autopause' && currentPlayer && currentInitialized) {
          await currentPlayer.pause();
        } else if (action === 'stop' && currentPlayer && currentInitialized) {
          await currentPlayer.pause();
          await currentPlayer.call('setCurrentTime', 0);
        }
      } catch (err) {
        console.warn(`Autopilot action "${action}" failed:`, err);
      }
    }
  },
};
